import { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, Smile, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_CONTENT } from '../data/content';

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const dropdownItemsRef = useRef([]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location.pathname]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isServicesOpen) {
          setIsServicesOpen(false);
          dropdownRef.current?.querySelector('a')?.focus();
        }
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
          mobileToggleRef.current?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isServicesOpen, isMobileMenuOpen]);

  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMobileMenuOpen || !mobileMenuRef.current) return;
    const menu = mobileMenuRef.current;
    const focusableEls = menu.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    if (focusableEls.length === 0) return;
    const first = focusableEls[0];
    const last = focusableEls[focusableEls.length - 1];

    const trap = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    menu.addEventListener('keydown', trap);
    first.focus();
    return () => menu.removeEventListener('keydown', trap);
  }, [isMobileMenuOpen]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsServicesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsServicesOpen(false), 200);
  };

  // Keyboard handler for desktop dropdown
  const handleDropdownKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Let the link navigate for Enter; Space toggles dropdown
      if (e.key === ' ') {
        e.preventDefault();
        setIsServicesOpen((prev) => !prev);
      }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsServicesOpen(true);
      // Focus first dropdown item after state update
      setTimeout(() => dropdownItemsRef.current[0]?.focus(), 50);
    }
  }, []);

  // Arrow key navigation inside dropdown
  const handleDropdownItemKeyDown = useCallback((e, index) => {
    const items = dropdownItemsRef.current;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = items[index + 1];
      if (next) next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        setIsServicesOpen(false);
        dropdownRef.current?.querySelector('a')?.focus();
      } else {
        items[index - 1]?.focus();
      }
    } else if (e.key === 'Escape') {
      setIsServicesOpen(false);
      dropdownRef.current?.querySelector('a')?.focus();
    }
  }, []);

  const isActive = (path) => location.pathname === path;
  const isServiceActive = location.pathname.startsWith('/szolgaltatasok');

  return (
    <nav
      data-testid="navigation"
      aria-label="Fő navigáció"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg py-3' : 'bg-white shadow-md py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 text-gray-800 hover:text-orange-600 transition-colors duration-200 no-underline"
            aria-label="Mosolytorna Logopédia - Főoldal"
          >
            <Smile className="w-7 h-7 text-orange-500" aria-hidden="true" />
            <span className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mosolytorna Logopédia
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-7" role="menubar">
            <Link
              to="/"
              data-testid="nav-home"
              role="menuitem"
              aria-current={isActive('/') ? 'page' : undefined}
              className={`font-medium text-sm xl:text-base transition-colors duration-200 hover:text-orange-600 no-underline ${
                isActive('/') ? 'text-orange-600' : 'text-gray-700'
              }`}
            >
              Főoldal
            </Link>
            <Link
              to="/rolam"
              data-testid="nav-about"
              role="menuitem"
              aria-current={isActive('/rolam') ? 'page' : undefined}
              className={`font-medium text-sm xl:text-base transition-colors duration-200 hover:text-orange-600 no-underline ${
                isActive('/rolam') ? 'text-orange-600' : 'text-gray-700'
              }`}
            >
              Rólam
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              role="none"
            >
              <Link
                to="/szolgaltatasok"
                data-testid="nav-services-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={isServicesOpen}
                aria-current={isActive('/szolgaltatasok') ? 'page' : undefined}
                onKeyDown={handleDropdownKeyDown}
                className={`font-medium flex items-center gap-1 transition-colors duration-200 hover:text-orange-600 no-underline ${
                  isServiceActive ? 'text-orange-600' : 'text-gray-700'
                }`}
              >
                Szolgáltatásaim
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isServicesOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </Link>

              {isServicesOpen && (
                <div className="absolute top-full left-0 pt-2 z-50" role="menu" aria-label="Szolgáltatások almenüje">
                  <div className="w-72 bg-white rounded-2xl shadow-2xl py-2 border border-gray-100 overflow-hidden">
                    {NAV_CONTENT.serviceLinks.map((link, index) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        role="menuitem"
                        ref={(el) => (dropdownItemsRef.current[index] = el)}
                        onKeyDown={(e) => handleDropdownItemKeyDown(e, index)}
                        data-testid={`nav-service-${link.href.split('/').pop()}`}
                        aria-current={location.pathname === link.href ? 'page' : undefined}
                        className={`block px-4 py-2.5 text-sm font-medium transition-colors duration-150 hover:bg-orange-50 hover:text-orange-600 mx-1 rounded-lg no-underline ${
                          location.pathname === link.href
                            ? 'text-orange-600 bg-orange-50'
                            : 'text-gray-700'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/illoolajok"
              data-testid="nav-oils"
              role="menuitem"
              aria-current={isActive('/illoolajok') ? 'page' : undefined}
              className={`font-medium text-sm xl:text-base transition-colors duration-200 hover:text-orange-600 no-underline ${
                isActive('/illoolajok') ? 'text-orange-600' : 'text-gray-700'
              }`}
            >
              Esszenciális olajok világa
            </Link>

            <Link
              to="/kapcsolat"
              data-testid="nav-contact"
              role="menuitem"
              aria-current={isActive('/kapcsolat') ? 'page' : undefined}
              className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-medium transition-colors duration-200 shadow-md hover:shadow-lg no-underline"
            >
              Kapcsolat
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            ref={mobileToggleRef}
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-gray-700 hover:text-orange-600 transition-colors duration-200"
            aria-label={isMobileMenuOpen ? 'Menü bezárása' : 'Menü megnyitása'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav
            id="mobile-nav-menu"
            ref={mobileMenuRef}
            data-testid="mobile-menu"
            aria-label="Mobil navigáció"
            className="lg:hidden mt-4 pb-4 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
            role="navigation"
          >
            <Link
              to="/"
              aria-current={isActive('/') ? 'page' : undefined}
              className={`block px-6 py-3 font-medium transition-colors duration-150 hover:bg-orange-50 hover:text-orange-600 no-underline ${
                isActive('/') ? 'text-orange-600 bg-orange-50' : 'text-gray-700'
              }`}
            >
              Főoldal
            </Link>
            <Link
              to="/rolam"
              aria-current={isActive('/rolam') ? 'page' : undefined}
              className={`block px-6 py-3 font-medium transition-colors duration-150 hover:bg-orange-50 hover:text-orange-600 no-underline ${
                isActive('/rolam') ? 'text-orange-600 bg-orange-50' : 'text-gray-700'
              }`}
            >
              Rólam
            </Link>

            {/* Mobile Services Section */}
            <div className="flex items-center border-b border-gray-50">
              <Link
                to="/szolgaltatasok"
                data-testid="mobile-services-link"
                aria-current={isActive('/szolgaltatasok') ? 'page' : undefined}
                className={`flex-1 px-6 py-3 font-medium transition-colors duration-150 hover:bg-orange-50 hover:text-orange-600 no-underline ${
                  isActive('/szolgaltatasok') ? 'text-orange-600 bg-orange-50' : 'text-gray-700'
                }`}
              >
                Szolgáltatásaim
              </Link>
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className="px-4 py-3 text-gray-500 hover:text-orange-600 hover:bg-orange-50 transition-colors duration-150"
                data-testid="mobile-services-toggle"
                aria-label={isMobileServicesOpen ? 'Szolgáltatások almenü bezárása' : 'Szolgáltatások almenü megnyitása'}
                aria-expanded={isMobileServicesOpen}
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isMobileServicesOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>

            {isMobileServicesOpen && (
              <ul className="bg-orange-50/50 list-none p-0 m-0" role="menu" aria-label="Szolgáltatások">
                {NAV_CONTENT.serviceLinks.map((link) => (
                  <li key={link.href} role="none">
                    <Link
                      to={link.href}
                      role="menuitem"
                      aria-current={location.pathname === link.href ? 'page' : undefined}
                      className={`block px-8 py-2.5 text-sm font-medium transition-colors duration-150 hover:bg-orange-100 hover:text-orange-600 no-underline ${
                        location.pathname === link.href ? 'text-orange-600' : 'text-gray-600'
                      }`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            <Link
              to="/illoolajok"
              aria-current={isActive('/illoolajok') ? 'page' : undefined}
              className={`block px-6 py-3 font-medium transition-colors duration-150 hover:bg-orange-50 hover:text-orange-600 no-underline ${
                isActive('/illoolajok') ? 'text-orange-600 bg-orange-50' : 'text-gray-700'
              }`}
            >
              Esszenciális olajok világa
            </Link>
            <div className="px-6 pt-2">
              <Link
                to="/kapcsolat"
                aria-current={isActive('/kapcsolat') ? 'page' : undefined}
                className="block text-center px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-medium transition-colors duration-200 no-underline"
              >
                Kapcsolat
              </Link>
            </div>
          </nav>
        )}
      </div>
    </nav>
  );
}
